import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import './App.css';

// Layout components
import { Header, Sidebar, HomePage, Footer, SplashScreen } from './components/layout';
import { AlertBar, DynamicModulePage, UnderConstruction } from './components/common';
import { NotificationProvider } from './context/NotificationContext';
import { EntraAuthProvider, ImpersonationAuthProvider, useAuth } from './context/AuthContext';
import { DatabaseProvider, useDatabase } from './context/DatabaseContext';
import MsalProviderWrapper from './auth/MsalProviderWrapper';
import { isEntraAuth } from './auth/authConfig';
import AccessDeniedPage from './pages/AccessDeniedPage';
import DatabasePickerPage from './pages/DatabasePickerPage';

// Development pages
import { SampleTableViewPage, TaskLogPage, DevPlanPage, SchemaViewerPage, FieldManagerPage, ClaudeNotesPage, RevLogPage, ListViewConfigPage, DevelopmentDashboardPage, IconBrowserPage, ImportTemplatePage, LookupValuesPage, DisplaySettingsPage, PowerQueryPage, SyncDashboardPage, StyleGuidePage } from './modules/development';
import { LookupProvider } from './context/LookupContext';

// Dashboard pages
import { DocumentationDashboardPage, NetworkingDashboardPage, EquipmentDashboardPage } from './modules/dashboards';

// Report pages
import { ReportViewerPage, ReportsPage } from './modules/reports';

// Administration pages
import { SecurityOverviewPage, SecurityDashboardPage, AdminUsersPage, RolesCapabilitiesPage, PermissionsPage, DatabasesPage, OrganizationsPage } from './modules/administration';

// Config
import { getPageDisplayName } from './config/navigationConfig';

// Pages that do NOT need a database slug (admin DB / dev tools)
const SLUG_FREE_PREFIXES = ['admin/', 'development-dashboard', 'sample-table-view', 'task-log', 'dev-plan', 'schema-viewer', 'field-manager', 'claude-notes', 'icon-browser', 'rev-log', 'standard-view-config', 'import-template', 'lookup-values', 'display-settings', 'database-sync', 'power-query', 'style-guide'];

const isSlugFreePage = (page) => SLUG_FREE_PREFIXES.some(p => page.startsWith(p));


/**
 * DbRedirect — redirects bare "/" to "/:defaultDbSlug/"
 */
const DbRedirect = () => {
  const lastSlug = localStorage.getItem('pmd-selected-db-slug');
  return <Navigate to={`/${lastSlug || 'gwa'}/`} replace />;
};


/**
 * AdminLayout — shared layout for slug-free pages (admin + dev tools).
 * Uses Outlet to render the matched child route.
 */
const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();

  const [currentPage, setCurrentPageState] = useState('');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pagePath = location.pathname.slice(1) || 'home';
    setCurrentPageState(pagePath);
  }, [location]);

  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      const lastSlug = localStorage.getItem('pmd-selected-db-slug') || 'gwa';
      navigate(`/${lastSlug}/${page === 'home' ? '' : page}`);
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => {
          const lastSlug = localStorage.getItem('pmd-selected-db-slug') || 'gwa';
          navigate(`/${lastSlug}/`);
        }}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Outlet />
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/**
 * AppContent — layout for data pages under /:dbSlug/*.
 */
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, accessDenied } = useAuth();
  const { dbSlug } = useDatabase();

  const [currentPage, setCurrentPageState] = useState('home');
  const [sidebarTarget, setSidebarTarget] = useState(null);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const pagePath = pathParts.slice(1).join('/') || 'home';
    setCurrentPageState(pagePath);
  }, [location]);

  const setCurrentPage = (page) => {
    if (isSlugFreePage(page)) {
      navigate(`/${page}`);
    } else {
      navigate(`/${dbSlug}/${page === 'home' ? '' : page}`);
    }
  };

  const clearSidebarTarget = () => setSidebarTarget(null);

  if (accessDenied) {
    return <AccessDeniedPage />;
  }

  return (
    <div className="app-container">
      <Header
        onNavigateHome={() => setCurrentPage('home')}
        setCurrentPage={setCurrentPage}
      />
      <AlertBar />
      <div className="main-layout">
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          sidebarTarget={sidebarTarget}
          clearSidebarTarget={clearSidebarTarget}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />
            <Route path="/home" element={<HomePage setCurrentPage={setCurrentPage} isAdmin={isAdmin} />} />

            {/* Dynamic module routes - project DB data */}
            <Route path="/buildings/:recordId?" element={<DynamicModulePage />} />
            <Route path="/drawings/:recordId?" element={<DynamicModulePage />} />
            <Route path="/users/:recordId?" element={<DynamicModulePage />} />
            <Route path="/source/:recordId?" element={<DynamicModulePage />} />
            <Route path="/discipline/:recordId?" element={<DynamicModulePage />} />
            <Route path="/commissioning-key/:recordId?" element={<DynamicModulePage />} />
            <Route path="/drawing-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/ip-address-sln/:recordId?" element={<DynamicModulePage />} />
            <Route path="/ip-address-dln/:recordId?" element={<DynamicModulePage />} />
            <Route path="/organization/:recordId?" element={<DynamicModulePage />} />
            <Route path="/card-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/networks-enet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/networks-serial/:recordId?" element={<DynamicModulePage />} />
            <Route path="/plc-msg-map/:recordId?" element={<DynamicModulePage />} />
            <Route path="/tags/:recordId?" element={<DynamicModulePage />} />
            <Route path="/eng-units/:recordId?" element={<DynamicModulePage />} />
            <Route path="/isa-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/index-sheet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/manufacturer/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panel-terminal/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panel-type/:recordId?" element={<DynamicModulePage />} />
            <Route path="/panels/:recordId?" element={<DynamicModulePage />} />
            <Route path="/pwr-dist/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-index/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/soo-equipment/:recordId?" element={<DynamicModulePage />} />
            <Route path="/subnet/:recordId?" element={<DynamicModulePage />} />
            <Route path="/rfi-log/:recordId?" element={<DynamicModulePage />} />
            <Route path="/system-code/:recordId?" element={<DynamicModulePage />} />
            <Route path="/transfer-party/:recordId?" element={<DynamicModulePage />} />
            <Route path="/valve-size/:recordId?" element={<DynamicModulePage />} />
            <Route path="/wire-type/:recordId?" element={<DynamicModulePage />} />

            {/* Report + Dashboard pages - project DB data */}
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/:reportSlug" element={<ReportViewerPage />} />
            <Route path="/documentation-dashboard" element={<DocumentationDashboardPage />} />
            <Route path="/networking-dashboard" element={<NetworkingDashboardPage />} />
            <Route path="/equipment-dashboard" element={<EquipmentDashboardPage />} />

            {/* Catch-all */}
            <Route path="*" element={
              <UnderConstruction pageName={getPageDisplayName(location.pathname.split('/').slice(2).join('/'))} />
            } />
          </Routes>
        </main>
      </div>
      <Footer currentPage={currentPage} isAdmin={isAdmin} />
    </div>
  );
};


/**
 * AppRoutes — all routes, rendered inside Router + Auth + Lookup providers.
 */
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<DbRedirect />} />
      <Route path="/access-denied" element={<AccessDeniedPage />} />
      <Route path="/select-database" element={<DatabasePickerPage />} />

      {/* Slug-free admin/dev pages — use AdminLayout with Outlet */}
      <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<SecurityDashboardPage />} />
        <Route path="/admin/security" element={<SecurityOverviewPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/roles" element={<RolesCapabilitiesPage />} />
        <Route path="/admin/permissions" element={<PermissionsPage />} />
        <Route path="/admin/databases" element={<DatabasesPage />} />
        <Route path="/admin/organizations" element={<OrganizationsPage />} />
        <Route path="/development-dashboard" element={<DevelopmentDashboardPage />} />
        <Route path="/sample-table-view" element={<SampleTableViewPage />} />
        <Route path="/task-log" element={<TaskLogPage />} />
        <Route path="/dev-plan" element={<DevPlanPage />} />
        <Route path="/schema-viewer" element={<SchemaViewerPage />} />
        <Route path="/field-manager" element={<FieldManagerPage />} />
        <Route path="/claude-notes" element={<ClaudeNotesPage />} />
        <Route path="/icon-browser" element={<IconBrowserPage />} />
        <Route path="/rev-log" element={<RevLogPage />} />
        <Route path="/standard-view-config" element={<ListViewConfigPage />} />
        <Route path="/import-template" element={<ImportTemplatePage />} />
        <Route path="/lookup-values" element={<LookupValuesPage />} />
        <Route path="/display-settings" element={<DisplaySettingsPage />} />
        <Route path="/database-sync" element={<SyncDashboardPage />} />
        <Route path="/power-query" element={<PowerQueryPage />} />
        <Route path="/style-guide" element={<StyleGuidePage />} />
        <Route path="/admin/app-users" element={<UnderConstruction pageName="Users" />} />
      </Route>

      {/* Data pages — need DB slug */}
      <Route path="/:dbSlug/*" element={
        <DatabaseProvider>
          <AppContent />
        </DatabaseProvider>
      } />
    </Routes>
  );
};


// Root App
const App = () => {
  if (isEntraAuth()) {
    return (
      <SplashScreen>
        <MsalProviderWrapper>
          <NotificationProvider>
            <EntraAuthProvider>
              <LookupProvider>
                <Router>
                  <AppRoutes />
                </Router>
              </LookupProvider>
            </EntraAuthProvider>
          </NotificationProvider>
        </MsalProviderWrapper>
      </SplashScreen>
    );
  }

  return (
    <SplashScreen>
      <NotificationProvider>
        <ImpersonationAuthProvider>
          <LookupProvider>
            <Router>
              <AppRoutes />
            </Router>
          </LookupProvider>
        </ImpersonationAuthProvider>
      </NotificationProvider>
    </SplashScreen>
  );
};

export default App;
